import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseSafeFormView, BaseView, BaseText } from '@/components/base'
import { RequestItem, AppIcons, StickyHeaderNavigation } from '@/components'
import { useGetRequests } from '@/domain/requests'
import { useAppPreferencesStore } from '@/store'
import { useTheme } from '@/hooks/useTheme'
import SafeAreaLayout from '@/components/layout/SafeAreaLayout'
import useOffsets from '@/hooks/useOffsets'

export const RequestsScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const offsets = useOffsets()
  const { displayCurrency } = useAppPreferencesStore()

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useGetRequests({ page_size: 20 })

  const allRequests = data?.pages?.flatMap((page) => page.requests) || []

  const handleBack = () => {
    navigate(-1)
  }

  const handleDeclineRequest = async (_requestId: string) => {
    // Request actions are now handled by RequestItem component using useRequestItemHandler
    // This callback is called after successful action to refresh the list
    refetch()
  }

  const handleAcceptRequest = async (_requestId: string) => {
    // Request actions are now handled by RequestItem component using useRequestItemHandler
    // This callback is called after successful action to refresh the list
    refetch()
  }

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }

  if (isError) {
    return (
      <BaseView
        flex={1}
        bgColor="background.screen"
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Sticky Header Navigation */}
        <StickyHeaderNavigation
          title={t('translation:requests') || 'Requests'}
          showBackButton={true}
          onBackPress={handleBack}
        />

        {/* Error Content */}
        <BaseView
          flex={1}
          center
          style={{
            paddingTop: '20px',
            paddingLeft: '16px',
            paddingRight: '16px',
            paddingBottom: '12px',
          }}
        >
          <BaseText color="label.error">{'Error loading requests'}</BaseText>
        </BaseView>
      </BaseView>
    )
  }

  return (
    <BaseView
      flex={1}
      bgColor="background.screen"
      style={{
        paddingTop: offsets.top + 70,
        paddingBottom: offsets.bottom,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Sticky Header Navigation */}
      <StickyHeaderNavigation
        title={t('translation:requests') || 'Requests'}
        showBackButton={true}
        onBackPress={handleBack}
      />

      {/* Scrollable Content */}
      <BaseView
        style={{
          flex: 1,
          overflow: 'hidden',
          // paddingTop: '70px',
          paddingLeft: '16px',
          paddingRight: '16px',
          // paddingBottom: '12px',
        }}
      >
        <BaseView
          style={{
            height: '100%',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {isLoading && allRequests.length === 0 ? (
            <BaseView flex={1} center>
              <BaseText color="label.description">
                {t('translation:loading') || 'Loading...'}
              </BaseText>
            </BaseView>
          ) : allRequests.length === 0 ? (
            <BaseView flex={1} center>
              <BaseText
                fontSize={16}
                fontFamily="regular"
                color="label.description"
                textAlign="center"
              >
                {'No requests found'}
              </BaseText>
            </BaseView>
          ) : (
            <BaseView gap={12}>
              {allRequests.map((request) => (
                <RequestItem
                  key={request.id}
                  request={request}
                  onDecline={() => handleDeclineRequest(request.transaction_id)}
                  onAccept={() => handleAcceptRequest(request.transaction_id)}
                  displayCurrency={displayCurrency}
                />
              ))}

              {/* Load More Button */}
              {hasNextPage && (
                <BaseView p={16}>
                  {isFetchingNextPage ? (
                    <BaseView center>
                      <BaseText color="label.description">
                        {t('translation:loading') || 'Loading...'}
                      </BaseText>
                    </BaseView>
                  ) : (
                    <BaseView
                      py={12}
                      px={24}
                      center
                      style={{
                        backgroundColor: 'transparent',
                        borderRadius: '12px',
                        border: `1px solid ${theme.colors.background.secondaryGray}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onClick={handleLoadMore}
                    >
                      <BaseText
                        fontSize={16}
                        fontFamily="medium"
                        color="label.default"
                      >
                        {'Load More'}
                      </BaseText>
                    </BaseView>
                  )}
                </BaseView>
              )}
            </BaseView>
          )}
        </BaseView>
      </BaseView>
    </BaseView>
  )
}
